import React, { useEffect, useState } from 'react';
import { Loader2, Search, Users } from 'lucide-react';
import { api, type UserSearchResult } from '../lib/api';
import { tierForRating } from '../lib/rating';
import { Avatar } from './Avatar';

/**
 * Buscador de cuentas por su nombre público. El texto vive en App para que
 * siga ahí al volver de un perfil.
 */
export const UserSearch: React.FC<{
  query: string;
  onQueryChange: (q: string) => void;
  onOpenUser: (id: string) => void;
}> = ({ query, onQueryChange, onOpenUser }) => {
  const [result, setResult] = useState<{ q: string; users: UserSearchResult[] } | null>(null);
  const q = query.trim();

  useEffect(() => {
    if (!q) return;
    let cancelled = false;
    // Se espera a que deje de teclear: una petición por pulsación no aporta nada.
    const timer = window.setTimeout(async () => {
      try {
        const res = await api.get<{ users: UserSearchResult[] }>(
          `/users/search?q=${encodeURIComponent(q)}`,
        );
        if (!cancelled) setResult({ q, users: res.users });
      } catch {
        if (!cancelled) setResult({ q, users: [] });
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [q]);

  // Sin texto se enseña a todos los registrados, por tandas.
  const [everyone, setEveryone] = useState<{ users: UserSearchResult[]; total: number } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadEveryone = async (offset: number) => {
    setLoadingMore(true);
    try {
      const res = await api.get<{ users: UserSearchResult[]; total: number }>(
        `/users/search?offset=${offset}`,
      );
      setEveryone((prev) => ({
        users: offset === 0 ? res.users : [...(prev?.users ?? []), ...res.users],
        total: res.total,
      }));
    } catch {
      setEveryone((prev) => prev ?? { users: [], total: 0 });
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!q && everyone === null) void loadEveryone(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const loading = q ? result?.q !== q : everyone === null;
  const users = loading ? [] : q ? (result?.users ?? []) : everyone!.users;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div className="text-center space-y-2">
        <Users className="w-10 h-10 mx-auto text-sky-400" />
        <h1 className="text-3xl font-display font-black">Jugadores</h1>
      </div>

      <label className="relative block">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
        <input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Busca a cualquier usuario por su nombre"
          maxLength={40}
          autoFocus
          className="w-full h-12 pl-10 pr-4 rounded-xl bg-ink-850 border border-ink-700 text-ink-100 placeholder:text-ink-600 outline-none transition-colors focus:border-sky-500 focus:ring-2 focus:ring-sky-500/25"
        />
      </label>

      {!q && everyone && (
        <p className="text-center text-ink-500 text-sm">
          {everyone.total} {everyone.total === 1 ? 'jugador registrado' : 'jugadores registrados'}
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-sky-500" />
        </div>
      ) : users.length === 0 ? (
        <div className="surface p-10 text-center text-ink-400">
          {q ? 'Nadie se llama así.' : 'Todavía no hay jugadores registrados.'}
        </div>
      ) : (
        <ul className="space-y-2">
          {users.map((u) => {
            const tier = tierForRating(u.dRating);
            return (
              <li key={u.id}>
                <button
                  onClick={() => onOpenUser(u.id)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border bg-ink-900/70 border-ink-800 hover:border-sky-600 transition-colors text-left"
                >
                  <Avatar config={u.avatar as never} size={40} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink-50 truncate">{u.username}</p>
                    <p className="text-ink-500 text-xs">
                      {tier.icon} {tier.label} · {u.totalGames}{' '}
                      {u.totalGames === 1 ? 'partida' : 'partidas'}
                    </p>
                  </div>
                  <span className="font-mono tabular-nums font-bold text-ink-100">{u.dRating}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {!q && everyone && everyone.users.length < everyone.total && (
        <div className="flex justify-center">
          <button
            onClick={() => void loadEveryone(everyone.users.length)}
            disabled={loadingMore}
            className="flex items-center gap-2 px-5 h-11 rounded-xl font-semibold text-sm border border-ink-700 bg-ink-850 text-ink-100 hover:bg-ink-800 transition-colors disabled:opacity-50"
          >
            {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
            Ver más ({everyone.total - everyone.users.length})
          </button>
        </div>
      )}
    </div>
  );
};
